// Native v4 CheckNativeAppUpdate — headless port of
// src/lib/offline/CheckNativeAppUpdate.tsx: on mount, dispatches the Mac
// auto-update check ('on_mount') and listens for the app's
// SetAutoUpdateStatus completion events; non-mac clients settle immediately.
import { useEffect } from 'react';

import { AppConfig } from '@/config/appConfig';
import { useEnabledFeatures } from '@/hooks/dataHelpers';
import { getAppType } from '@/lib/offline/OfflineAppHelper';
import { useOfflineAppStore } from '@/store/offlineAppStore';

import { dispatchToUpdateNativeMacApp } from './updateAppDispatch';

export interface CheckNativeAppUpdateProps {
  handleSetAutoUpdateChecking?: (value: boolean) => void;
}

const UPDATE_COMPLETION_STATUSES = [
  'skip',
  'error',
  'complete',
  'failedToDownload',
  'alreadyCheckedTheUpdates',
];

export const CheckNativeAppUpdate = ({
  handleSetAutoUpdateChecking,
}: CheckNativeAppUpdateProps) => {
  const { executeMethods } = useOfflineAppStore();
  const appType = getAppType();
  const appRegion = AppConfig.region;
  const enabledFeatures = useEnabledFeatures();

  useEffect(() => {
    if (appType !== 'mac') {
      handleSetAutoUpdateChecking?.(false);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleSetAutoUpdateStatus = (e: any) => {
      if (UPDATE_COMPLETION_STATUSES.includes(e.detail)) {
        handleSetAutoUpdateChecking?.(false);
      }
    };

    window.addEventListener('SetAutoUpdateStatus', handleSetAutoUpdateStatus);

    dispatchToUpdateNativeMacApp(
      'on_mount',
      appType,
      appRegion,
      enabledFeatures,
      executeMethods,
      () => handleSetAutoUpdateChecking?.(false)
    );

    return () => {
      window.removeEventListener('SetAutoUpdateStatus', handleSetAutoUpdateStatus);
    };
    // v3 dep list kept ([appType]) — one dispatch per app type resolution.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appType]);

  return null;
};
